import { Box, Paper, SimpleGrid, Stack, Text, Button, Flex } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";
import DashboardCard from "./DashboardCard";
import TitleComponent from "@/components/shared/TitleComponent";
import { useVerificationModal } from "@/components/layout/AppLayout";
import {
  buildDashboardReturnTo,
  DASHBOARD_REQUESTS_TAB,
  parseDashboardRequestFilter,
  type RequestFilter,
} from "./dashboardTab.const";
import { workspaceBackLinkState } from "@/shared/utils/workspaceBackNavigation.util";
import RequestFilterHeaderSelect from "./RequestFilterHeaderSelect";
import { useInterestRequests } from "@/pages/interests/hooks/useInterestRequests";
import { usePreferredPartners } from "@/pages/interests/hooks/usePreferredPartners";
import InterestRequestsTabContent from "@/pages/interests/components/InterestRequestsTabContent";

const DASHBOARD_PANEL_BG = "#f7f9fe";

export default function DashboardPage() {
  const isMobileHeader = useMediaQuery("(max-width: 47.99em)");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestFilter = parseDashboardRequestFilter(
    searchParams.get("tab"),
    searchParams.get("filter")
  );
  const { userDetails, collaborationSidebarCounts, shortlistedIds } = useUserContext();
  const { openVerificationModal } = useVerificationModal();
  const userName = userDetails?.name?.trim() || "User";
  const companyStatus = userDetails?.company?.status;
  const canShowVerifyButton = companyStatus !== "VERIFIED" && companyStatus !== "SYSTEM_VERIFIED";

  const dashboardWorkspaceLinkState = workspaceBackLinkState(buildDashboardReturnTo(requestFilter));

  const interestRequests = useInterestRequests({
    activeRequestFilter: requestFilter,
    scrollOnFilterChange: true,
    workspaceLinkState: dashboardWorkspaceLinkState,
  });

  const preferredPartners = usePreferredPartners({
    activeRequestFilter: requestFilter,
  });

  const setRequestFilter = useCallback(
    (filter: RequestFilter) => {
      setSearchParams({ tab: DASHBOARD_REQUESTS_TAB, filter }, { replace: true });
    },
    [setSearchParams]
  );

  const pageHeight = isMobileHeader ? "calc(100dvh - 72px)" : "calc(100dvh - 32px)";

  return (
    <Box
      w="100%"
      maw="100%"
      style={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        height: pageHeight,
        maxHeight: pageHeight,
        overflow: "hidden",
        backgroundColor: "#f7f9fe",
      }}
    >
      <Box style={{ flexShrink: 0 }}>
        <TitleComponent
          title={`Welcome! ${userName}`}
          description="Here is your dashboard overview."
          rightSection={<AppHeaderUserActions />}
        />
      </Box>

      <Box
        px="lg"
        pt={0}
        pb={0}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          {canShowVerifyButton && (
            <Paper p="md" radius="md" withBorder style={{ flexShrink: 0 }}>
              <Flex justify="space-between" align="center" gap="sm" wrap="wrap">
                <Box>
                  <Text fw={700}>Complete Company Verification</Text>
                  <Text size="sm" c="dimmed">
                    Verify your company details from dashboard. Credits are deducted only when verification is processed.
                  </Text>
                </Box>
                <Button onClick={openVerificationModal}>Verify Company</Button>
              </Flex>
            </Paper>
          )}

          <SimpleGrid
            cols={{ base: 3, sm: 2, lg: 3 }}
            spacing={{ base: "xs", md: "md" }}
            style={{ flexShrink: 0 }}
          >
            <Box>
              <DashboardCard
                title="Shortlist"
                count={shortlistedIds.size}
                description="Companies you have shortlisted"
                onClick={() => navigate("/shortlist")}
                compact={isMobileHeader}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Active Collaborations"
                compactTitle="Active"
                count={collaborationSidebarCounts.active}
                description="Ongoing collaborations with partners"
                onClick={() => navigate("/collaborations")}
                compact={isMobileHeader}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Closed Collaborations"
                compactTitle="Closed"
                count={collaborationSidebarCounts.closed}
                description="Completed or inactive collaborations"
                onClick={() => navigate("/collaborations")}
                compact={isMobileHeader}
              />
            </Box>
          </SimpleGrid>

          <Box
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <RequestFilterHeaderSelect value={requestFilter} onChange={setRequestFilter} />

            <Paper
              radius="md"
              p={0}
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: DASHBOARD_PANEL_BG,
                borderRadius: "var(--mantine-radius-md)",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
              }}
            >
              <InterestRequestsTabContent
                requests={interestRequests}
                preferredPartners={preferredPartners}
                requestFilter={requestFilter}
              />
            </Paper>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
